import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, LogOut } from "lucide-react";

import { AccountPanel } from "@/components/admin/AccountPanel";
import { EmployeesPanel } from "@/components/admin/EmployeesPanel";
import { FortnoxPanel } from "@/components/admin/FortnoxPanel";
import { LoginForm } from "@/components/admin/LoginForm";
import { TimeLogPanel } from "@/components/admin/TimeLogPanel";
import { VacationPanel } from "@/components/admin/VacationPanel";
import { LiveClock } from "@/components/timeclock/LiveClock";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authApi } from "@/lib/api";

const TABS = [
  { value: "tid", label: "Tidrapport & export" },
  { value: "ledighet", label: "Ledighet" },
  { value: "personal", label: "Personal" },
  { value: "fortnox", label: "Fortnox" },
  { value: "konto", label: "Konto" },
] as const;

export function AdminPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: "/admin" });

  const session = useQuery({
    queryKey: ["me"],
    queryFn: authApi.me,
    retry: false,
    staleTime: 0,
  });

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.clear();
      navigate({ to: "/" });
    },
  });

  if (session.isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Kontrollerar inloggning…</p>
      </main>
    );
  }

  const admin = session.data?.admin ?? null;

  if (!admin) {
    return <LoginForm onSuccess={() => session.refetch()} />;
  }

  const activeTab = TABS.some((tab) => tab.value === search.flik) ? search.flik! : "tid";

  return (
    <main className="min-h-screen px-5 py-6 sm:px-8">
      <header className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="press flex size-10 items-center justify-center rounded-full bg-card text-muted-foreground shadow-panel"
            aria-label="Tillbaka till panelen"
          >
            <ArrowLeft className="size-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Adminläge</h1>
            <LiveClock compact />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-muted-foreground sm:inline">{admin.name}</span>
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
          >
            <LogOut className="size-4" /> Logga ut
          </Button>
        </div>
      </header>

      <div className="mx-auto mt-6 max-w-6xl">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            navigate({ to: "/admin", search: { flik: value }, replace: true })
          }
        >
          <TabsList className="h-auto flex-wrap rounded-full">
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="rounded-full px-5">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="tid" className="mt-5">
            <TimeLogPanel />
          </TabsContent>
          <TabsContent value="ledighet" className="mt-5">
            <VacationPanel />
          </TabsContent>
          <TabsContent value="personal" className="mt-5">
            <EmployeesPanel />
          </TabsContent>
          <TabsContent value="fortnox" className="mt-5">
            <FortnoxPanel notice={{ ok: search.fortnox === "ok", error: search.fortnox_fel }} />
          </TabsContent>
          <TabsContent value="konto" className="mt-5">
            <AccountPanel me={admin} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
